/**
 * AgentKbFiles — документы, таблицы и Google-источники агента.
 *
 * Раньше это был SelfKbFiles, прибитый к нашей Тоне. С появлением специалистов
 * (08.08) такой блок нужен каждому агенту, а отличается он только тем, чей slug
 * стоит в адресе — поэтому slug стал параметром, а копия исчезла.
 *
 * Текст из файла или Google-документа попадает в раздел базы знаний ЭТОГО
 * агента: у специалиста свои прайсы, у основного — свои регламенты.
 */

import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, FileSpreadsheet, FileText, Link2, Loader2, Paperclip, RefreshCw, Trash2, Unlink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { agentsApi } from '@/api/client'

type Kind = 'table' | 'document'

const COPY: Record<Kind, { hint: string; accept: string; empty: string }> = {
  table: {
    hint: 'Прайс-листы, каталоги, номенклатура. Из таблицы извлекаются строки и попадают в раздел базы знаний.',
    accept: '.xlsx,.xlsm,.csv',
    empty: 'Таблиц пока нет.',
  },
  document: {
    hint: 'Договоры, регламенты, инструкции. Из документа извлекается текст и попадает в раздел базы знаний.',
    accept: '.docx,.pdf,.txt,.md',
    empty: 'Документов пока нет.',
  },
}

function fmtSize(b: number): string {
  if (b < 1024) return `${b} Б`
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} КБ`
  return `${(b / 1024 / 1024).toFixed(1)} МБ`
}

export function AgentKbFiles({ slug, kind }: { slug: string; kind: Kind }) {
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [showConnect, setShowConnect] = useState(false)
  const [url, setUrl] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const qc = useQueryClient()

  const { data: files, isLoading } = useQuery({
    queryKey: ['agent-files', slug],
    queryFn: () => agentsApi.files(slug),
    staleTime: 30 * 1000,
  })
  const { data: sources } = useQuery({
    queryKey: ['agent-google', slug],
    queryFn: () => agentsApi.googleList(slug),
    staleTime: 30 * 1000,
  })

  const copy = COPY[kind]
  const Icon = kind === 'table' ? FileSpreadsheet : FileText
  const shown = (files ?? []).filter((f) => f.kind === kind)
  const wantKind = kind === 'table' ? 'gsheet' : 'gdoc'
  const docs = (sources ?? []).filter((d) => d.kind === wantKind)

  async function refresh() {
    await qc.invalidateQueries({ queryKey: ['agent-files', slug] })
    await qc.invalidateQueries({ queryKey: ['agent-google', slug] })
    // Текст уехал в разделы — редактор базы знаний должен это увидеть.
    await qc.invalidateQueries({ queryKey: ['agent', slug] })
    await qc.invalidateQueries({ queryKey: ['self-agent'] })
  }

  async function run(key: string, fn: () => Promise<string | null>) {
    setBusy(key); setError(null); setNote(null)
    try {
      const msg = await fn()
      await refresh()
      if (msg) setNote(msg)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось.')
    } finally { setBusy(null) }
  }

  /** Подключить ссылку и остаться в форме: источников обычно несколько подряд
   *  (прайс разложен по листам), и закрывать форму после каждого — мучение. */
  function connect() {
    run('connect', async () => {
      const r = await agentsApi.googleConnect(slug, url.trim())
      setUrl('')
      return `«${r.title}» подключён: ${r.extracted_chars.toLocaleString('ru-RU')} знаков. Можно вставить следующую ссылку.`
    })
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground leading-relaxed">{copy.hint}</p>

      <div className="relative flex flex-wrap items-center gap-2">
        <Button size="sm" className="h-8 gap-1.5 text-xs" disabled={busy !== null}
                onClick={() => setMenuOpen((v) => !v)}>
          {busy === 'upload' ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className="text-sm leading-none">+</span>}
          Подключить {kind === 'table' ? 'таблицу' : 'документ'}
          <ChevronDown className="h-3 w-3" />
        </Button>

        {menuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
            <div className="absolute left-0 top-9 z-20 w-72 rounded-lg border bg-background p-1.5 shadow-lg">
              <button type="button" className="flex w-full items-start gap-3 rounded px-2 py-2 text-left hover:bg-accent"
                      onClick={() => { setMenuOpen(false); inputRef.current?.click() }}>
                <Paperclip className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>
                  <span className="block text-sm">Импортировать файл</span>
                  <span className="block text-[11px] text-muted-foreground">Файл с вашего компьютера</span>
                </span>
              </button>
              <button type="button" className="flex w-full items-start gap-3 rounded px-2 py-2 text-left hover:bg-accent"
                      onClick={() => { setMenuOpen(false); setShowConnect(true) }}>
                <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>
                  <span className="block text-sm">Подключиться к Google-{kind === 'table' ? 'таблице' : 'документу'}</span>
                  <span className="block text-[11px] text-muted-foreground">По ссылке с открытым доступом</span>
                </span>
              </button>
            </div>
          </>
        )}
        <input ref={inputRef} type="file" className="sr-only" accept={copy.accept}
               onChange={(e) => {
                 const f = e.target.files?.[0]; e.target.value = ''
                 if (f) run('upload', async () => {
                   const r = await agentsApi.attachFile(slug, f)
                   return `Текст добавлен в раздел «${r.title}»: ${r.extracted_chars.toLocaleString('ru-RU')} знаков.`
                 })
               }} />
      </div>

      {showConnect && (
        <div className="space-y-2 rounded-md border bg-slate-50/60 p-3">
          <input autoFocus className="w-full rounded border bg-background px-2 py-1.5 text-sm"
                 placeholder="https://docs.google.com/…" value={url}
                 disabled={busy !== null}
                 onChange={(e) => setUrl(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter' && url.trim() && busy === null) connect() }} />
          <p className="text-[11px] text-muted-foreground">
            Документ должен быть открыт «Всем, у кого есть ссылка — Читатель».
            {kind === 'table' && ' Листы одной книги подключаются по отдельности — скопируйте ссылку с открытого листа.'}
          </p>
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs" disabled={busy !== null || !url.trim()}
                    onClick={connect}>
              {busy === 'connect' && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              Подключить
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowConnect(false)}>
              Готово
            </Button>
          </div>
        </div>
      )}

      {docs.length > 0 && (
        <div className="divide-y rounded-md border">
          <p className="bg-slate-50/60 px-3 py-1.5 text-[11px] text-muted-foreground">
            Подключено из Google: {docs.length}
          </p>
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 px-3 py-2">
              <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <a href={d.url} target="_blank" rel="noreferrer" className="block truncate text-sm hover:underline">{d.title}</a>
                <p className={`text-[11px] ${d.last_error ? 'text-amber-700' : 'text-muted-foreground'}`}>
                  {d.last_error || (d.last_synced_at ? `обновлён ${new Date(d.last_synced_at).toLocaleString('ru-RU')}` : 'ещё не читался')}
                </p>
              </div>
              <button type="button" title="Перечитать" disabled={busy === d.id}
                      className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                      onClick={() => run(d.id, async () => {
                        const r = await agentsApi.googleSync(slug, d.id)
                        return `«${r.title}» перечитан: ${r.extracted_chars.toLocaleString('ru-RU')} знаков.`
                      })}>
                {busy === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              </button>
              <button type="button" title="Отвязать" disabled={busy === d.id}
                      className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                      onClick={() => run(d.id, async () => { await agentsApi.googleDisconnect(slug, d.id); return null })}>
                <Unlink className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {note && <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">{note}</div>}
      {error && <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">{error}</div>}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Загружаю…
        </div>
      ) : shown.length === 0 ? (
        <p className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">{copy.empty}</p>
      ) : (
        <div className="divide-y rounded-md border">
          {shown.map((f) => (
            <div key={f.filename} className="flex items-center gap-3 px-3 py-2">
              <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{f.filename}</p>
                <p className="text-[11px] text-muted-foreground">
                  {fmtSize(f.size_bytes)} · {new Date(f.uploaded_at * 1000).toLocaleString('ru-RU')}
                </p>
              </div>
              <button type="button" title="Удалить файл" disabled={busy === f.filename}
                      className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                      onClick={() => run(f.filename, async () => { await agentsApi.removeFile(slug, f.filename); return null })}>
                {busy === f.filename ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-muted-foreground">
        Раздел создаётся из имени файла автоматически. Удаление убирает только файл:
        извлечённый текст остаётся в разделе, по нему агент и отвечает.
      </p>
    </div>
  )
}
